import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';

// Components
import Login from './components/Login';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { AlertManager } from './components/CustomAlert';
import AppInitializer from './components/AppInitializer';
import { getSectionPrivileges, getFirstAllowedPath } from './utils/positionsApi';
import { useSelector } from 'react-redux';

// Pages
import Dashboard from './pages/Dashboard';
import ActivityLog from './pages/ActivityLog/ActivityLog';
import AddStudentApplication from './pages/Students/AddStudentApplication';
import AssignSession from './pages/Students/AssignSession';
import StudentList from './pages/Students/StudentList';
import StudentTransfer from './pages/Students/StudentTransfer';
import TransferHistory from './pages/Students/TransferHistory';
import BulkSendDocument from './pages/Students/BulkSendDocument';
import PreviousCourse from './pages/Students/PreviousCourse';
import AllAppliedCandidates from './pages/Applications/AllAppliedCandidates';
import AddAttendance from './pages/Attendance/AddAttendance';
import AttendanceReport from './pages/Attendance/AttendanceReport';
import Billing from './pages/Finance/Billing';

// Instructor Management
import AddEmployee from './pages/Instructors/AddEmployee';
import AllInstructors from './pages/Instructors/AllInstructors';

// Staff Management
import AddStaff from './pages/Staff/AddStaff';
import AllStaffs from './pages/Staff/AllStaffs';
import Positions from './pages/Staff/Positions';
import UserManagement from './pages/Staff/UserManagement';
import CareerCoaches from './pages/Staff/CareerCoaches';

// Sessions and Courses
import Sessions from './pages/Sessions/Sessions';
import Courses from './pages/Courses/Courses';

// Notifications
import SendAnnouncement from './pages/Notifications/SendAnnouncement';
import AnnouncementsList from './pages/Notifications/AnnouncementsList';
import SendSMS from './pages/Notifications/SendSMS';
import AllSMS from './pages/Notifications/AllSMS';
import SMSChat from './pages/Notifications/SMSChat';
import SendPassword from './pages/Notifications/SendPassword';

// Profile Management
import AcademicYear from './pages/Profile/AcademicYear';
import Departments from './pages/Profile/Departments';
import Semesters from './pages/Profile/Semesters';
import Designations from './pages/Profile/Designations';
import Locations from './pages/Profile/Locations';
import DaysPreferred from './pages/Profile/DaysPreferred';
import Individuals from './pages/Profile/Individuals';
import Workforces from './pages/Profile/Workforces';
import MasterData from './pages/Profile/MasterData';
import Particulars from './pages/Profile/Particulars';

// Update Course & Session
import UpdateCourseSession from './pages/UpdateCourseSession';
import StudentAcknowledgmentForm from './pages/Students/StudentAcknowledgmentForm';
import StudentEmergencyContactForm from './pages/Students/StudentEmergencyContactForm';
import Vouchers from './pages/Students/Vouchers';
import { StudentEnrollmentFormProvider } from './pages/Students/StudentEnrollmentFormContext';
import UniformSizeForm from './pages/Students/UniformSizeForm';
import StudentIdForm from './pages/Students/StudentIdForm';
import MedicalEvaluationForm from './pages/Students/MedicalEvaluationForm';
import BackgroundCheckForm from './pages/Students/BackgroundCheckForm';
import AttendanceSheetForm from './pages/Students/AttendanceSheetForm';
import EnrollmentAgreementForm from './pages/Students/EnrollmentAgreementForm';
import ConsentReleaseInfoForm from './pages/Students/ConsentReleaseInfoForm';
import MediaReleaseForm from './pages/Students/MediaReleaseForm';
import CovidVaccinationAttestationForm from './pages/Students/CovidVaccinationAttestationForm';
import CellPhonePolicyForm from './pages/Students/CellPhonePolicyForm';
import MedicalSuppliesAcknowledgmentForm from './pages/Students/MedicalSuppliesAcknowledgmentForm';

// Redirect to first allowed route (or login if not authenticated)
const DefaultRedirect = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={getFirstAllowedPath(user)} replace />;
};

// Wrapper component to extract id parameter
const MedicalEvaluationFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <MedicalEvaluationForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter
const BackgroundCheckFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <BackgroundCheckForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for ConsentReleaseInfoForm
const ConsentReleaseInfoFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <ConsentReleaseInfoForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for MediaReleaseForm
const MediaReleaseFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <MediaReleaseForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for CovidVaccinationAttestationForm
const CovidVaccinationAttestationFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <CovidVaccinationAttestationForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for CellPhonePolicyForm
const CellPhonePolicyFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <CellPhonePolicyForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for MedicalSuppliesAcknowledgmentForm
const MedicalSuppliesAcknowledgmentFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <MedicalSuppliesAcknowledgmentForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for EnrollmentAgreementForm
const EnrollmentAgreementFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <EnrollmentAgreementForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for StudentAcknowledgmentForm
const StudentAcknowledgmentFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <StudentAcknowledgmentForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for StudentEmergencyContactForm
const StudentEmergencyContactFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <StudentEmergencyContactForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for UniformSizeForm
const UniformSizeFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <UniformSizeForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for StudentIdForm
const StudentIdFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <StudentIdForm />
    </StudentEnrollmentFormProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AppInitializer>
          <Router basename="/admin">
          {/* <Router> */}
            <AlertManager />
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/students/:studentId/attendance-sheet" element={<AttendanceSheetForm />} />
              <Route path="/consent-release-info-form/:id" element={<ConsentReleaseInfoFormWrapper />} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('dashboard')}>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/activity-log" element={
                <ProtectedRoute requiredPermission="activity_log_read">
                  <Layout>
                    <ActivityLog />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Sessions and Courses Routes */}
              <Route path="/sessions" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('sessions')}>
                  <Layout>
                    <Sessions />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/courses" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('sessions')}>
                  <Layout>
                    <Courses />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Notifications Routes */}
              <Route path="/notifications/send-announcement" element={
                <ProtectedRoute>
                  <Layout>
                    <SendAnnouncement />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/announcements-list" element={
                <ProtectedRoute>
                  <Layout>
                    <AnnouncementsList />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/send-sms" element={
                <ProtectedRoute>
                  <Layout>
                    <SendSMS />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/all-sms" element={
                <ProtectedRoute>
                  <Layout>
                    <AllSMS />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/sms-chat" element={
                <ProtectedRoute>
                  <Layout>
                    <SMSChat />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/send-password" element={
                <ProtectedRoute>
                  <Layout>
                    <SendPassword />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Student Routes */}
              <Route path="/students/add" element={
                <ProtectedRoute>
                  <Layout>
                    <AddStudentApplication />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/students/list" element={
                <ProtectedRoute>
                  <Layout>
                    <StudentList />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/students/assign-session" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('students')}>
                  <Layout>
                    <AssignSession />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/students/transfer" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('students')}>
                  <Layout>
                    <StudentTransfer />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/students/transfer-history" element={
                <ProtectedRoute>
                  <Layout>
                    <TransferHistory />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/students/bulk-send-document" element={
                <ProtectedRoute>
                  <Layout>
                    <BulkSendDocument />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/students/previous-course" element={
                <ProtectedRoute>
                  <Layout>
                    <PreviousCourse />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/students/vouchers" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('students')}>
                  <Layout>
                    <Vouchers />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Instructor Routes */}
              <Route path="/instructors/add" element={
                <ProtectedRoute>
                  <Layout>
                    <AddEmployee />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/instructors/all" element={
                <ProtectedRoute>
                  <Layout>
                    <AllInstructors />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Staff Routes */}
              <Route path="/staff/add" element={
                <ProtectedRoute>
                  <Layout>
                    <AddStaff />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/staff/all" element={
                <ProtectedRoute>
                  <Layout>
                    <AllStaffs />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/staff/positions" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('staff')}>
                  <Layout>
                    <Positions />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/staff/users" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('staff')}>
                  <Layout>
                    <UserManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/staff/career-coaches" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('staff')}>
                  <Layout>
                    <CareerCoaches />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Attendance Routes */}
              <Route path="/attendance/add" element={
                <ProtectedRoute>
                  <Layout>
                    <AddAttendance />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* <Route path="/attendance/dashboard" element={
                <ProtectedRoute>
                  <Layout>
                    <AttendanceDashboard />
                  </Layout>
                </ProtectedRoute>
              } /> */}
              
              <Route path="/attendance/report" element={
                <ProtectedRoute>
                  <Layout>
                    <AttendanceReport />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Finance Routes */}
              <Route path="/finance/billing" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('finance')}>
                  <Layout>
                    <Billing />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Applications Routes */}
              <Route path="/applications/applied-candidates" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('applications')}>
                  <Layout>
                    <AllAppliedCandidates />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/applications/all-applied-candidates" element={
                <ProtectedRoute requiredPermissions={getSectionPrivileges('applications')}>
                  <Layout>
                    <AllAppliedCandidates />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Profile Management Routes */}
              <Route path="/profile/academic-year" element={
                <ProtectedRoute>
                  <Layout>
                    <AcademicYear />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile/departments" element={
                <ProtectedRoute>
                  <Layout>
                    <Departments />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile/semesters" element={
                <ProtectedRoute>
                  <Layout>
                    <Semesters />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile/designations" element={
                <ProtectedRoute>
                  <Layout>
                    <Designations />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile/locations" element={
                <ProtectedRoute>
                  <Layout>
                    <Locations />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile/days-preferred" element={
                <ProtectedRoute>
                  <Layout>
                    <DaysPreferred />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile/individuals" element={
                <ProtectedRoute>
                  <Layout>
                    <Individuals />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile/workforces" element={
                <ProtectedRoute>
                  <Layout>
                    <Workforces />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile/master-data" element={
                <ProtectedRoute>
                  <Layout>
                    <MasterData />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile/particulars" element={
                <ProtectedRoute>
                  <Layout>
                    <Particulars />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Update Course & Session Route */}
              <Route path="/update-course-session" element={
                <ProtectedRoute>
                  <Layout>
                    <UpdateCourseSession />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Student Acknowledgment Route */}
              <Route path="/student-acknowledgment/:id" element={<StudentAcknowledgmentFormWrapper />} />
              {/* Emergency Contact Form (view only) */}
              <Route path="/student-emergency-contact/:id" element={<StudentEmergencyContactFormWrapper />} />
              <Route path="/uniform-size-form/:id" element={<UniformSizeFormWrapper />} />
              <Route path="/student-id-form/:id" element={<StudentIdFormWrapper />} />

              {/* Medical Evaluation Form */}
              <Route path="/medical-evaluation-form/:id" element={<MedicalEvaluationFormWrapper />} />

              {/* Background Check Form */}
              <Route path="/background-check-form/:id" element={<BackgroundCheckFormWrapper />} />

              {/* Enrollment Agreement Form */}
              <Route path="/enrollment-agreement-form/:id" element={<EnrollmentAgreementFormWrapper />} />

              {/* Media Release Form */}
              <Route path="/media-release-form/:id" element={<MediaReleaseFormWrapper />} />

              {/* COVID-19 Vaccination Attestation Form */}
              <Route path="/covid-vaccination-attestation-form/:id" element={<CovidVaccinationAttestationFormWrapper />} />

              {/* Cell Phone Policy Form */}
              <Route path="/cell-phone-policy-form/:id" element={<CellPhonePolicyFormWrapper />} />

              {/* Medical Supplies Acknowledgment Form */}
              <Route path="/medical-supplies-acknowledgment-form/:id" element={<MedicalSuppliesAcknowledgmentFormWrapper />} />

              {/* Default redirect to first allowed route (or login) */}
              <Route path="/" element={<DefaultRedirect />} />
              
              {/* Catch all route */}
              <Route path="*" element={<DefaultRedirect />} />
            </Routes>
          </Router>
        </AppInitializer>
      </PersistGate>
    </Provider>
  );
}

export default App;
